import { useEffect, useMemo, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { Loader } from '@/components/Loader'
import ErrorAlert from '@/components/ErrorAlert'
import InfoModal from '@/components/InfoModal'
import MoneyInput from '@/components/MoneyInput'
import TextInput from '@/components/TextInput'
import Checkbox from '@/components/Checkbox'
import Radio from '@/components/Radio'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useFeatureFlags } from '@/rules/get-feature-flags'

import { EmptyState } from '../components/empty-state'
import { useMobileNavStore } from '../api/use-mobile-nav-store'
import { ProfileSubNav } from './components/profile-subnav'
import { useGTKY } from './api/get-gtky'
import { usePostGTKY } from './api/post-gtky'
import { GTKYRequestTypes } from './api/schema'

const RISK_OPTIONS = [
  { value: 'high_risk_return', label: 'High Risk, High Return' },
  { value: 'moderate_risk_return', label: 'Moderate Risk, Moderate Return' },
  { value: 'low_risk_return', label: 'Low Risk, Low Return' },
]

const HOLD_PERIOD_OPTIONS = [
  { value: 'hold_0_2', label: 'Short-term (0-2 years)' },
  { value: 'hold_2_5', label: 'Medium-term (2-5 years)' },
  { value: 'hold_5_7', label: 'Longer-term (5-7 years)' },
  { value: 'hold_7_', label: 'More than 7 years' },
]

const CURRENT_INVESTMENTS_OPTIONS = [
  { value: 'invest_pub_reits', label: 'Public REITs' },
  { value: 'invest_priv_reits', label: 'Private REITs' },
  { value: 'invest_re_syndications', label: 'Real estate syndications' },
  {
    value: 'invest_own_comm_re',
    label: 'I operate my own commercial real estate investments',
  },
  {
    value: 'invest_op_resi_re',
    label: 'I operate my own residential real estate investments',
  },
  { value: 'invest_none', label: 'None of the above' },
]

const PROPERTY_TYPE_LABELS = {
  interest_residential: 'Residential',
  interest_multifam: 'Multi-Family/Apartments',
  interest_office: 'Office',
  interest_industrial: 'Industrial',
  interest_retail: 'Retail',
  interest_retail_nnn: 'NNN',
  interest_hospitality: 'Hospitality/Hotels',
  interest_mob_home: 'Mobile Home Parks',
  interest_storage: 'Self-Storage',
  interest_medical: 'Medical Office',
  interest_student: 'Student Housing',
  interest_senior: 'Senior Housing',
  interest_other: 'Other',
} as const

const PROPERTY_TYPE_OPTIONS = [
  { value: 'interest_residential', label: 'Residential' },
  { value: 'interest_multifam', label: 'Multi-Family/Apartments' },
  { value: 'interest_office', label: 'Office' },
  { value: 'interest_industrial', label: 'Industrial' },
  { value: 'interest_retail', label: 'Retail' },
  { value: 'interest_hospitality', label: 'Hospitality/Hotels' },
  { value: 'interest_mob_home', label: 'Mobile Home Parks' },
  { value: 'interest_storage', label: 'Self-Storage' },
  { value: 'interest_other', label: 'Other' },
]
const VISIBLE_PROPERTY_TYPE_VALUES = new Set(
  PROPERTY_TYPE_OPTIONS.map((option) => option.value)
)

const EMPLOYMENT_OPTIONS = [
  { value: 'employed', label: 'Employed' },
  { value: 'self_employed', label: 'Self-Employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'student', label: 'Student' },
  { value: 'not_employed', label: 'Not Employed' },
]

const GTKY_TEXT_FIELD_LIMITS = {
  employer: 255,
  employmentPositionHeld: 128,
  employmentIndustry: 80,
  sourceOfIncome: 255,
  sourceOfWealth: 80,
} as const
const GTKY_INTEGER_FIELD_LIMITS = {
  specificIncome: 2147483647,
  specificNetWorth: 2147483647,
} as const

const EMPLOYMENT_FIELD_CONFIG = {
  employed: {
    employerLabel: 'Employer',
    positionLabel: 'Current Position Held',
    industryLabel: 'Industry',
    showEmployer: true,
    showPosition: true,
    showIndustry: false,
  },
  self_employed: {
    employerLabel: 'Business Name',
    positionLabel: 'Your Title',
    industryLabel: 'Industry / Business Type',
    showEmployer: true,
    showPosition: true,
    showIndustry: true,
  },
  retired: {
    employerLabel: 'Most Recent Employer',
    positionLabel: 'Most Recent Position Held',
    industryLabel: 'Industry / Business Type',
    showEmployer: true,
    showPosition: true,
    showIndustry: true,
  },
  student: {
    employerLabel: 'School',
    positionLabel: 'Position Held',
    industryLabel: 'Industry',
    showEmployer: true,
    showPosition: false,
    showIndustry: false,
  },
  not_employed: {
    employerLabel: 'Employer',
    positionLabel: 'Position Held',
    industryLabel: 'Industry',
    showEmployer: false,
    showPosition: false,
    showIndustry: false,
  },
} as const

type EmploymentType = keyof typeof EMPLOYMENT_FIELD_CONFIG

const EMPLOYMENT_TYPE_VALUES = new Set<EmploymentType>(
  Object.keys(EMPLOYMENT_FIELD_CONFIG) as EmploymentType[]
)

type GTKYFormValues = Omit<
  GTKYRequestTypes,
  'id' | 'specificIncome' | 'specificNetWorth'
> & {
  need1031Choice: 'yes' | 'no' | ''
  specificIncome: string
  specificNetWorth: string
}

const EMPTY_FORM_VALUES: GTKYFormValues = {
  risk: [],
  holdPeriod: [],
  currentInvestments: [],
  propertyTypes: [],
  need1031: undefined,
  need1031Choice: '',
  employmentType: '',
  employer: '',
  employmentPositionHeld: '',
  employmentIndustry: '',
  sourceOfIncome: '',
  sourceOfWealth: '',
  specificIncome: '',
  specificNetWorth: '',
  draft: false,
}

const formatIntegerFieldValue = (value: number | undefined) => {
  return typeof value === 'number' ? String(value) : ''
}

const normalizeEmploymentType = (
  value: string | undefined
): EmploymentType | '' => {
  if (!value) return ''

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

  return EMPLOYMENT_TYPE_VALUES.has(normalized as EmploymentType)
    ? (normalized as EmploymentType)
    : ''
}

const formatLegacyPropertyTypeLabel = (value: string) => {
  return `Legacy value (${value})`
}

const parseWholeDollarValue = (value: string | undefined) => {
  const normalized = (value ?? '').trim()
  if (!normalized) {
    return { status: 'empty' as const }
  }

  if (normalized.includes('.')) {
    return { status: 'invalid' as const }
  }

  const digits = normalized.replace(/[$,\s]/g, '')
  if (!/^\d+$/.test(digits)) {
    return { status: 'invalid' as const }
  }

  const parsed = Number.parseInt(digits, 10)
  if (Number.isNaN(parsed)) {
    return { status: 'invalid' as const }
  }

  return {
    status: 'valid' as const,
    value: parsed,
  }
}

const parseIntegerFieldValue = (value: string) => {
  const parsed = parseWholeDollarValue(value)
  return parsed.status === 'valid' ? parsed.value : undefined
}

const toggleArrayValue = (current: string[], value: string) => {
  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value]
}

const toggleCurrentInvestmentValue = (current: string[], value: string) => {
  if (value === 'invest_none') {
    return current.includes(value) ? [] : [value]
  }

  return toggleArrayValue(
    current.filter((item) => item !== 'invest_none'),
    value
  )
}

const Gtky = () => {
  const nav = useNavigate()
  const { toast } = useToast()
  const updateMobileNav = useMobileNavStore((state) => state.updateMobileNav)
  const { data: featureFlags, isLoading: flagsLoading } = useFeatureFlags()
  const gtkyEnabled = !!featureFlags?.ff_dashboard_gtky
  const { data, isLoading, isError } = useGTKY(gtkyEnabled)
  const { mutateAsync: postGTKY, isLoading: isSaving } = usePostGTKY()

  const defaultValues = useMemo<GTKYFormValues>(() => {
    if (!data?.request) return EMPTY_FORM_VALUES

    const normalizedEmploymentType = normalizeEmploymentType(
      data.request.employmentType
    )

    return {
      ...EMPTY_FORM_VALUES,
      ...data.request,
      employmentType: normalizedEmploymentType,
      specificIncome: formatIntegerFieldValue(data.request.specificIncome),
      specificNetWorth: formatIntegerFieldValue(data.request.specificNetWorth),
      need1031Choice:
        typeof data.request.need1031 === 'boolean'
          ? data.request.need1031
            ? 'yes'
            : 'no'
          : '',
    }
  }, [data])

  const propertyTypeOptions = useMemo(() => {
    const selectedHiddenOptions = defaultValues.propertyTypes
      .filter((value) => !VISIBLE_PROPERTY_TYPE_VALUES.has(value))
      .map((value) => ({
        value,
        label:
          PROPERTY_TYPE_LABELS[value as keyof typeof PROPERTY_TYPE_LABELS] ||
          formatLegacyPropertyTypeLabel(value),
      }))

    return [...PROPERTY_TYPE_OPTIONS, ...selectedHiddenOptions]
  }, [defaultValues.propertyTypes])

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<GTKYFormValues>({
    defaultValues,
  })
  const employmentType = watch('employmentType')
  const previousEmploymentTypeRef = useRef(employmentType)

  const employmentFieldConfig =
    EMPLOYMENT_FIELD_CONFIG[
      normalizeEmploymentType(employmentType) || 'employed'
    ]

  useEffect(() => {
    updateMobileNav(false)
  }, [updateMobileNav])

  useEffect(() => {
    reset(defaultValues)
    previousEmploymentTypeRef.current = defaultValues.employmentType
  }, [defaultValues, reset])

  useEffect(() => {
    if (previousEmploymentTypeRef.current === employmentType) return

    previousEmploymentTypeRef.current = employmentType

    if (!employmentFieldConfig.showEmployer) {
      setValue('employer', '', { shouldDirty: true, shouldValidate: false })
    }

    if (!employmentFieldConfig.showPosition) {
      setValue('employmentPositionHeld', '', {
        shouldDirty: true,
        shouldValidate: false,
      })
    }

    if (!employmentFieldConfig.showIndustry) {
      setValue('employmentIndustry', '', {
        shouldDirty: true,
        shouldValidate: false,
      })
    }
  }, [employmentFieldConfig, employmentType, setValue])

  const onSubmit = handleSubmit(async (formValues) => {
    const payload: Omit<GTKYRequestTypes, 'id'> = {
      risk: formValues.risk,
      holdPeriod: formValues.holdPeriod,
      currentInvestments: formValues.currentInvestments,
      propertyTypes: formValues.propertyTypes,
      need1031:
        formValues.need1031Choice === ''
          ? undefined
          : formValues.need1031Choice === 'yes',
      employmentType: formValues.employmentType,
      employer: formValues.employer,
      employmentPositionHeld: formValues.employmentPositionHeld,
      employmentIndustry: formValues.employmentIndustry,
      sourceOfIncome: formValues.sourceOfIncome,
      sourceOfWealth: formValues.sourceOfWealth,
      specificIncome: parseIntegerFieldValue(formValues.specificIncome),
      specificNetWorth: parseIntegerFieldValue(formValues.specificNetWorth),
      draft: false,
    }

    const response = await postGTKY(payload)
    const normalizedEmploymentType = normalizeEmploymentType(
      response.request?.employmentType
    )
    reset({
      ...EMPTY_FORM_VALUES,
      ...response.request,
      employmentType: normalizedEmploymentType,
      specificIncome: formatIntegerFieldValue(response.request?.specificIncome),
      specificNetWorth: formatIntegerFieldValue(
        response.request?.specificNetWorth
      ),
      need1031Choice:
        typeof response.request?.need1031 === 'boolean'
          ? response.request.need1031
            ? 'yes'
            : 'no'
          : '',
    })

    toast({
      variant: 'success',
      description: 'Information updated.',
      position: 'top',
    })
  })

  const validateRequired = (
    value: string | string[] | undefined,
    message = 'is required'
  ) => {
    if (Array.isArray(value)) return value.length > 0 || message
    return !!value || message
  }

  const validateMaxLength = (value: string | undefined, maxLength: number) => {
    if (!value) return true
    return value.length <= maxLength || `${maxLength} characters maximum`
  }

  const validateEmploymentField = (
    value: string | undefined,
    fieldName: 'employer' | 'employmentPositionHeld' | 'employmentIndustry'
  ) => {
    const visibility = {
      employer: employmentFieldConfig.showEmployer,
      employmentPositionHeld: employmentFieldConfig.showPosition,
      employmentIndustry: employmentFieldConfig.showIndustry,
    }

    if (!visibility[fieldName]) return true

    const requiredValidation = validateRequired(value)
    if (requiredValidation !== true) return requiredValidation

    return validateMaxLength(value, GTKY_TEXT_FIELD_LIMITS[fieldName])
  }

  const validateBoundedRequiredField = (
    value: string | undefined,
    fieldName: 'sourceOfIncome' | 'sourceOfWealth'
  ) => {
    const requiredValidation = validateRequired(value)
    if (requiredValidation !== true) return requiredValidation

    return validateMaxLength(value, GTKY_TEXT_FIELD_LIMITS[fieldName])
  }

  const validateCurrencyField = (
    value: string | undefined,
    fieldName: 'specificIncome' | 'specificNetWorth'
  ) => {
    const parsed = parseWholeDollarValue(value)
    if (parsed.status === 'empty') return 'is required'
    if (parsed.status === 'invalid') return 'Enter a whole dollar amount'

    return (
      parsed.value <= GTKY_INTEGER_FIELD_LIMITS[fieldName] ||
      `Maximum value is $${GTKY_INTEGER_FIELD_LIMITS[fieldName].toLocaleString()}`
    )
  }

  if (flagsLoading) {
    return (
      <div className="flex items-center justify-center h-[35vh]">
        <Loader />
      </div>
    )
  }

  if (!featureFlags?.ff_dashboard_gtky) {
    return <Navigate to="/dashboard/profile" replace />
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[35vh]">
        <Loader />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center w-full h-[35vh]">
        <EmptyState
          iconClasses="fa-regular fa-bomb fa-3x text-content-black"
          copy="There was an error while retrieving your information."
          linkCopy="Back to Profile"
          linkTo="/dashboard/profile"
        />
      </div>
    )
  }

  return (
    <>
      <div
        className="h-10 px-6 justify-start bg-bg-lighter items-center gap-3 flex md:hidden"
        onClick={() => {
          nav('/dashboard/profile')
        }}
      >
        <button className="h-4 flex-col justify-center items-center inline-flex">
          <i className="fa-solid fa-angle-left" />
        </button>
        <div className="text-base mt-[2px]">Profile</div>
      </div>
      <div className="w-full h-full p-6 md:p-0">
        <h1 className="text-27 md:text-31 font-bold self-start">
          <span className="hidden md:inline">Profile</span>
          <span className="inline md:hidden">Getting to Know You</span>
        </h1>
        <div className="hidden md:block my-10">
          <ProfileSubNav />
        </div>
        <div className="mt-6 md:mt-0 mb-8 text-16 text-content-black">
          Keep your investing preferences and background current.
        </div>
        {data?.request && !data.request.draft && (
          <div className="mb-6 inline-flex items-center rounded bg-bg-lighter px-3 py-2 text-sm font-medium text-content-black">
            Submitted
          </div>
        )}
        <form onSubmit={onSubmit} className="w-full max-w-[760px]">
          {Object.keys(errors).length > 0 && (
            <ErrorAlert text="Please fix the error(s) below:" />
          )}
          <div className="border border-border-normal rounded">
            <div className="border-b border-bg-light p-6">
              <h2 className="text-21 font-bold mb-2">Investment Preferences</h2>
              <p className="text-16 text-content-black">
                Tell us how you think about risk, timing, and the kinds of real
                estate investments that interest you.
              </p>
            </div>
            <div className="p-6 space-y-8">
              <Controller
                name="risk"
                control={control}
                rules={{ validate: (value) => validateRequired(value) }}
                render={({ field, fieldState }) => (
                  <div>
                    <div className="mb-2 text-base font-normal text-content-black">
                      Risk Profile
                    </div>
                    <div className="flex flex-col gap-px">
                      {RISK_OPTIONS.map((option) => (
                        <Checkbox
                          key={option.value}
                          name={`risk-${option.value}`}
                          label={option.label}
                          checked={field.value.includes(option.value)}
                          onChange={() =>
                            field.onChange(
                              toggleArrayValue(field.value, option.value)
                            )
                          }
                        />
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-xs text-alert">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                name="holdPeriod"
                control={control}
                rules={{ validate: (value) => validateRequired(value) }}
                render={({ field, fieldState }) => (
                  <div>
                    <div className="mb-2 text-base font-normal text-content-black">
                      Holding Period
                    </div>
                    <div className="flex flex-col gap-px">
                      {HOLD_PERIOD_OPTIONS.map((option) => (
                        <Checkbox
                          key={option.value}
                          name={`hold-period-${option.value}`}
                          label={option.label}
                          checked={field.value.includes(option.value)}
                          onChange={() =>
                            field.onChange(
                              toggleArrayValue(field.value, option.value)
                            )
                          }
                        />
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-xs text-alert">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                name="currentInvestments"
                control={control}
                rules={{ validate: (value) => validateRequired(value) }}
                render={({ field, fieldState }) => (
                  <div>
                    <div className="mb-2 text-base font-normal text-content-black">
                      Current Investments
                    </div>
                    <div className="flex flex-col gap-px">
                      {CURRENT_INVESTMENTS_OPTIONS.map((option) => (
                        <Checkbox
                          key={option.value}
                          name={`current-investments-${option.value}`}
                          label={option.label}
                          checked={field.value.includes(option.value)}
                          onChange={() =>
                            field.onChange(
                              toggleCurrentInvestmentValue(
                                field.value,
                                option.value
                              )
                            )
                          }
                        />
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-xs text-alert">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                name="propertyTypes"
                control={control}
                rules={{ validate: (value) => validateRequired(value) }}
                render={({ field, fieldState }) => (
                  <div>
                    <div className="mb-2 text-base font-normal text-content-black">
                      Property Types
                    </div>
                    <div className="grid gap-px md:grid-cols-2">
                      {propertyTypeOptions.map((option) => (
                        <Checkbox
                          key={option.value}
                          name={`property-types-${option.value}`}
                          label={option.label}
                          checked={field.value.includes(option.value)}
                          onChange={() =>
                            field.onChange(
                              toggleArrayValue(field.value, option.value)
                            )
                          }
                        />
                      ))}
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-xs text-alert">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                name="need1031Choice"
                control={control}
                rules={{ validate: (value) => validateRequired(value) }}
                render={({ field, fieldState }) => (
                  <div>
                    <div className="mb-2 text-base font-normal text-content-black">
                      Interested in 1031 Exchange Opportunities
                    </div>
                    <div className="flex flex-col gap-px">
                      <Radio
                        {...field}
                        value="yes"
                        label="Yes"
                        checked={field.value === 'yes'}
                      />
                      <Radio
                        {...field}
                        value="no"
                        label="No"
                        checked={field.value === 'no'}
                      />
                    </div>
                    {fieldState.error && (
                      <p className="mt-1 text-xs text-alert">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          <div className="border border-border-normal rounded mt-6">
            <div className="border-b border-bg-light p-6">
              <h2 className="text-21 font-bold mb-2">Background</h2>
              <p className="text-16 text-content-black">
                Share the details we use to keep your record current.
              </p>
            </div>
            <div className="p-6 grid gap-6 md:grid-cols-2">
              <Controller
                name="employmentType"
                control={control}
                rules={{ validate: (value) => validateRequired(value) }}
                render={({ field, fieldState }) => (
                  <div className="md:col-span-2">
                    <label
                      className={`mb-2 block text-base ${
                        fieldState.error ? 'text-alert' : 'text-content-black'
                      }`}
                    >
                      Employment Type
                    </label>
                    <select
                      className={`h-[42px] w-full rounded border px-3 py-1.5 text-16 focus:outline-none focus:ring-1 focus:border-primary ${
                        fieldState.error ? 'border-alert' : 'border-input'
                      }`}
                      {...field}
                    >
                      <option value="">Select one</option>
                      {EMPLOYMENT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {fieldState.error && (
                      <p className="mt-1 text-xs text-alert">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              {employmentFieldConfig.showEmployer && (
                <Controller
                  name="employer"
                  control={control}
                  rules={{
                    validate: (value) =>
                      validateEmploymentField(value, 'employer'),
                  }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={employmentFieldConfig.employerLabel}
                      errorMessage={fieldState.error}
                      maxLength={GTKY_TEXT_FIELD_LIMITS.employer}
                      {...field}
                    />
                  )}
                />
              )}
              {employmentFieldConfig.showPosition && (
                <Controller
                  name="employmentPositionHeld"
                  control={control}
                  rules={{
                    validate: (value) =>
                      validateEmploymentField(value, 'employmentPositionHeld'),
                  }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={employmentFieldConfig.positionLabel}
                      errorMessage={fieldState.error}
                      maxLength={GTKY_TEXT_FIELD_LIMITS.employmentPositionHeld}
                      {...field}
                    />
                  )}
                />
              )}
              {employmentFieldConfig.showIndustry && (
                <Controller
                  name="employmentIndustry"
                  control={control}
                  rules={{
                    validate: (value) =>
                      validateEmploymentField(value, 'employmentIndustry'),
                  }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={employmentFieldConfig.industryLabel}
                      errorMessage={fieldState.error}
                      maxLength={GTKY_TEXT_FIELD_LIMITS.employmentIndustry}
                      {...field}
                    />
                  )}
                />
              )}
              <Controller
                name="sourceOfIncome"
                control={control}
                rules={{
                  validate: (value) =>
                    validateBoundedRequiredField(value, 'sourceOfIncome'),
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    label="Source of Income"
                    errorMessage={fieldState.error}
                    maxLength={GTKY_TEXT_FIELD_LIMITS.sourceOfIncome}
                    {...field}
                  />
                )}
              />
              <Controller
                name="sourceOfWealth"
                control={control}
                rules={{
                  validate: (value) =>
                    validateBoundedRequiredField(value, 'sourceOfWealth'),
                }}
                render={({ field, fieldState }) => (
                  <div className="md:col-span-2">
                    <TextInput
                      label="Source of Wealth"
                      errorMessage={fieldState.error}
                      maxLength={GTKY_TEXT_FIELD_LIMITS.sourceOfWealth}
                      {...field}
                    />
                  </div>
                )}
              />
              <Controller
                name="specificIncome"
                control={control}
                rules={{
                  validate: (value) =>
                    validateCurrencyField(value, 'specificIncome'),
                }}
                render={({ field, fieldState }) => (
                  <MoneyInput
                    label={
                      <span>
                        Current Annual Income{' '}
                        <InfoModal
                          modalContent={
                            <div className="px-6 md:px-8 md:pt-4">
                              <h2 className="mb-6 text-xl font-medium text-content-black">
                                Current Annual Income
                              </h2>
                              <p className="mb-6 font-sans text-16 font-normal text-content-black">
                                &quot;Annual income&quot; must be calculated as
                                set forth in Rule 501(a) under the Securities
                                Act of 1933, as amended, which requires natural
                                persons to consider their income in the two most
                                recent years and a reasonable expectation of
                                income for the current year.
                              </p>
                            </div>
                          }
                        />
                      </span>
                    }
                    placeholder="$"
                    error={fieldState.error}
                    {...field}
                  />
                )}
              />
              <Controller
                name="specificNetWorth"
                control={control}
                rules={{
                  validate: (value) =>
                    validateCurrencyField(value, 'specificNetWorth'),
                }}
                render={({ field, fieldState }) => (
                  <MoneyInput
                    label={
                      <span>
                        Current Net Worth{' '}
                        <InfoModal
                          modalContent={
                            <div className="px-6 md:px-8 md:pt-4">
                              <h2 className="mb-6 text-xl font-medium text-content-black">
                                Current Net Worth
                              </h2>
                              <p className="mb-6 font-sans text-16 font-normal text-content-black">
                                The meaning of &quot;net worth&quot; means the
                                excess of total assets at fair market value
                                excluding your primary residence minus total
                                liabilities.
                              </p>
                            </div>
                          }
                        />
                      </span>
                    }
                    placeholder="$"
                    error={fieldState.error}
                    {...field}
                  />
                )}
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col md:flex-row gap-6">
            <Button variant="RM" size="RM" type="submit" disabled={isSaving}>
              Submit
            </Button>
            <Link to="/dashboard/profile" className="md:w-full">
              <Button
                variant="RMSecondary"
                size="RMSecondary"
                type="button"
                disabled={isSaving}
              >
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </>
  )
}

export default Gtky
